/**
 * 通知和错误处理工具集
 * 合并 error-utils.ts + message-utils.ts
 * 提供统一的错误处理和消息管理功能
 *
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

import { ElMessage, ElNotification } from 'element-plus';

// ============= 错误码枚举 =============

/**
 * 错误严重程度
 */
export enum Severity {
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal'
}

/**
 * 统一错误码
 */
export enum ErrorCode {
  // 通用错误
  UNKNOWN = 'UNKNOWN',
  NETWORK = 'NETWORK',
  TIMEOUT = 'TIMEOUT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  RATE_LIMIT = 'RATE_LIMIT',
  SERVER = 'SERVER',
  VALIDATION = 'VALIDATION',
  CONFLICT = 'CONFLICT',
  CANCELLED = 'CANCELLED',

  // TTS相关错误
  TTS_ENGINE_UNAVAILABLE = 'TTS_ENGINE_UNAVAILABLE',
  TTS_QUOTA_EXCEEDED = 'TTS_QUOTA_EXCEEDED',
  TTS_TEXT_TOO_LONG = 'TTS_TEXT_TOO_LONG',
  TTS_VOICE_NOT_FOUND = 'TTS_VOICE_NOT_FOUND',
  TTS_INVALID_SSML = 'TTS_INVALID_SSML',
  TTS_TASK_FAILED = 'TTS_TASK_FAILED',

  // 文件相关错误
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  FILE_FORMAT_UNSUPPORTED = 'FILE_FORMAT_UNSUPPORTED',
  FILE_UPLOAD_FAILED = 'FILE_UPLOAD_FAILED',
  FILE_DOWNLOAD_FAILED = 'FILE_DOWNLOAD_FAILED',
  FILE_SIGNED_URL_EXPIRED = 'FILE_SIGNED_URL_EXPIRED',

  // 支付相关错误
  PAY_METHOD_INVALID = 'PAY_METHOD_INVALID',
  PAY_3DS_REQUIRED = 'PAY_3DS_REQUIRED',
  PAY_INSUFFICIENT_FUNDS = 'PAY_INSUFFICIENT_FUNDS',
  PAY_GATEWAY_ERROR = 'PAY_GATEWAY_ERROR',

  // 业务相关错误
  QUOTA_EXHAUSTED = 'QUOTA_EXHAUSTED',
  MEMBERSHIP_REQUIRED = 'MEMBERSHIP_REQUIRED'
}

/**
 * 错误恢复建议
 */
export type RecoveryHint =
  | 'RETRY_WITH_BACKOFF'
  | 'CHECK_NETWORK'
  | 'REAUTH'
  | 'ASK_UPGRADE_PLAN'
  | 'REDUCE_PAYLOAD'
  | 'CHANGE_VOICE'
  | 'REFRESH_SIGNED_URL'
  | 'RESELECT_PAYMENT'
  | 'CONTACT_SUPPORT'
  | 'SHOW_DIALOG';

/**
 * 应用错误接口
 */
export interface AppError extends Error {
  code: ErrorCode;
  severity: Severity;
  httpStatus?: number;
  retryable?: boolean;
  correlationId?: string;
  cause?: unknown;
  messageForUser?: string;
  recovery?: RecoveryHint[];
  feature?: 'SDK' | 'TTS' | 'FILE' | 'PAY' | 'AUTH';
}

// ============= 消息类型定义 =============

export type MessageType = 'success' | 'warning' | 'info' | 'error';

export interface MessageConfig {
  type: MessageType;
  title?: string;
  message: string;
  duration?: number;
  showClose?: boolean;
  center?: boolean;
  dangerouslyUseHTMLString?: boolean;
  customClass?: string;
  offset?: number;
  appendTo?: HTMLElement | string;
  zIndex?: number;
  preventDuplicate?: boolean;
}

export interface MessageTypeConfig {
  duration: number;
  showClose: boolean;
  center: boolean;
}

export interface QueuedMessage {
  id: string;
  type: MessageType;
  message: string;
  timestamp: number;
  config?: Partial<MessageConfig>;
}

export interface MessageStats {
  total: number;
  byType: Record<MessageType, number>;
  recent: number; // 最近5分钟的消息数量
}

// ============= 错误处理 =============

/**
 * 错误映射配置类型
 */
type ErrorMapping = {
  severity: Severity;
  retryable: boolean;
  defaultMsg: string;
  recovery: RecoveryHint[];
};

/**
 * 统一错误映射表
 */
const ERROR_MAPPINGS: Record<ErrorCode, ErrorMapping> = {
  // 通用错误
  [ErrorCode.UNKNOWN]: {
    severity: Severity.ERROR,
    retryable: false,
    defaultMsg: '发生未知错误',
    recovery: ['CONTACT_SUPPORT']
  },
  [ErrorCode.NETWORK]: {
    severity: Severity.WARN,
    retryable: true,
    defaultMsg: '网络连接失败，请检查网络设置',
    recovery: ['CHECK_NETWORK', 'RETRY_WITH_BACKOFF']
  },
  [ErrorCode.TIMEOUT]: {
    severity: Severity.WARN,
    retryable: true,
    defaultMsg: '请求超时，请稍后重试',
    recovery: ['RETRY_WITH_BACKOFF']
  },
  [ErrorCode.UNAUTHORIZED]: {
    severity: Severity.WARN,
    retryable: false,
    defaultMsg: '登录已过期，请重新登录',
    recovery: ['REAUTH', 'SHOW_DIALOG']
  },
  [ErrorCode.FORBIDDEN]: {
    severity: Severity.WARN,
    retryable: false,
    defaultMsg: '权限不足，无法执行此操作',
    recovery: ['SHOW_DIALOG']
  },
  [ErrorCode.NOT_FOUND]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '请求的资源不存在',
    recovery: []
  },
  [ErrorCode.RATE_LIMIT]: {
    severity: Severity.WARN,
    retryable: true,
    defaultMsg: '请求过于频繁，请稍后再试',
    recovery: ['RETRY_WITH_BACKOFF']
  },
  [ErrorCode.SERVER]: {
    severity: Severity.ERROR,
    retryable: true,
    defaultMsg: '服务器错误，请稍后重试',
    recovery: ['RETRY_WITH_BACKOFF']
  },
  [ErrorCode.VALIDATION]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '输入数据格式不正确',
    recovery: ['SHOW_DIALOG']
  },
  [ErrorCode.CONFLICT]: {
    severity: Severity.WARN,
    retryable: true,
    defaultMsg: '资源冲突，请稍后重试',
    recovery: ['RETRY_WITH_BACKOFF']
  },
  [ErrorCode.CANCELLED]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '操作已取消',
    recovery: []
  },

  // TTS相关错误
  [ErrorCode.TTS_ENGINE_UNAVAILABLE]: {
    severity: Severity.ERROR,
    retryable: true,
    defaultMsg: 'TTS引擎不可用，已自动排队重试',
    recovery: ['RETRY_WITH_BACKOFF', 'CHANGE_VOICE']
  },
  [ErrorCode.TTS_QUOTA_EXCEEDED]: {
    severity: Severity.WARN,
    retryable: false,
    defaultMsg: 'TTS额度不足',
    recovery: ['ASK_UPGRADE_PLAN', 'SHOW_DIALOG']
  },
  [ErrorCode.TTS_TEXT_TOO_LONG]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '文本过长，请缩短后再试',
    recovery: ['REDUCE_PAYLOAD', 'SHOW_DIALOG']
  },
  [ErrorCode.TTS_VOICE_NOT_FOUND]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '找不到指定音色，请更换',
    recovery: ['CHANGE_VOICE']
  },
  [ErrorCode.TTS_INVALID_SSML]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: 'SSML格式不正确',
    recovery: ['SHOW_DIALOG']
  },
  [ErrorCode.TTS_TASK_FAILED]: {
    severity: Severity.ERROR,
    retryable: true,
    defaultMsg: 'TTS任务执行失败',
    recovery: ['RETRY_WITH_BACKOFF']
  },

  // 文件相关错误
  [ErrorCode.FILE_TOO_LARGE]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '文件过大，请压缩或拆分',
    recovery: ['REDUCE_PAYLOAD', 'SHOW_DIALOG']
  },
  [ErrorCode.FILE_FORMAT_UNSUPPORTED]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '文件格式不支持',
    recovery: ['SHOW_DIALOG']
  },
  [ErrorCode.FILE_UPLOAD_FAILED]: {
    severity: Severity.ERROR,
    retryable: true,
    defaultMsg: '文件上传失败，请重试',
    recovery: ['RETRY_WITH_BACKOFF', 'CHECK_NETWORK']
  },
  [ErrorCode.FILE_DOWNLOAD_FAILED]: {
    severity: Severity.ERROR,
    retryable: true,
    defaultMsg: '文件下载失败，请重试',
    recovery: ['RETRY_WITH_BACKOFF', 'CHECK_NETWORK']
  },
  [ErrorCode.FILE_SIGNED_URL_EXPIRED]: {
    severity: Severity.WARN,
    retryable: true,
    defaultMsg: '下载链接已过期，正在刷新',
    recovery: ['REFRESH_SIGNED_URL', 'RETRY_WITH_BACKOFF']
  },

  // 支付相关错误
  [ErrorCode.PAY_METHOD_INVALID]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '支付方式不可用',
    recovery: ['RESELECT_PAYMENT', 'SHOW_DIALOG']
  },
  [ErrorCode.PAY_3DS_REQUIRED]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '需要完成3D验证',
    recovery: ['RESELECT_PAYMENT', 'SHOW_DIALOG']
  },
  [ErrorCode.PAY_INSUFFICIENT_FUNDS]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '余额不足或信用额度不够',
    recovery: ['RESELECT_PAYMENT', 'SHOW_DIALOG']
  },
  [ErrorCode.PAY_GATEWAY_ERROR]: {
    severity: Severity.ERROR,
    retryable: true,
    defaultMsg: '支付网关异常，请稍后重试',
    recovery: ['RETRY_WITH_BACKOFF']
  },

  // 业务相关错误
  [ErrorCode.QUOTA_EXHAUSTED]: {
    severity: Severity.WARN,
    retryable: false,
    defaultMsg: '配额已用尽，请升级或明日再试',
    recovery: ['ASK_UPGRADE_PLAN', 'SHOW_DIALOG']
  },
  [ErrorCode.MEMBERSHIP_REQUIRED]: {
    severity: Severity.INFO,
    retryable: false,
    defaultMsg: '该功能需会员等级支持',
    recovery: ['ASK_UPGRADE_PLAN', 'SHOW_DIALOG']
  }
};



// ============= 错误构建和推断 =============

/**
 * 构建AppError对象
 */
export function buildAppError(partial: Partial<AppError> & { code: ErrorCode; message?: string }): AppError {
  const mapping = ERROR_MAPPINGS[partial.code] ?? ERROR_MAPPINGS[ErrorCode.UNKNOWN];
  const err = new Error(partial.message ?? mapping.defaultMsg) as AppError;
  err.code = partial.code;
  err.severity = partial.severity ?? mapping.severity;
  err.retryable = partial.retryable ?? mapping.retryable;
  err.httpStatus = partial.httpStatus;
  err.correlationId = partial.correlationId;
  err.cause = partial.cause;
  err.messageForUser = partial.messageForUser ?? mapping.defaultMsg;
  err.recovery = partial.recovery ?? mapping.recovery;
  err.feature = partial.feature ?? 'SDK';
  return err;
}

/**
 * 根据HTTP状态码推断错误码
 */
export function getErrorCodeByHttpStatus(status?: number): ErrorCode {
  if (!status) return ErrorCode.NETWORK;
  if (status === 401) return ErrorCode.UNAUTHORIZED;
  if (status === 403) return ErrorCode.FORBIDDEN;
  if (status === 404) return ErrorCode.NOT_FOUND;
  if (status === 409) return ErrorCode.CONFLICT;
  if (status === 422) return ErrorCode.VALIDATION;
  if (status === 429) return ErrorCode.RATE_LIMIT;
  if (status >= 500) return ErrorCode.SERVER;
  return ErrorCode.UNKNOWN;
}

/**
 * 综合推断错误码（结合HTTP状态、业务码、错误消息）
 */
export function inferErrorCode(payload?: {
  httpStatus?: number;
  bizCode?: string;
  message?: string;
}): ErrorCode {
  const { httpStatus, bizCode, message } = payload ?? {};

  // 后端业务码优先
  if (bizCode === 'TTS_ENGINE_DOWN') return ErrorCode.TTS_ENGINE_UNAVAILABLE;
  if (bizCode === 'QUOTA_EXHAUSTED') return ErrorCode.QUOTA_EXHAUSTED;
  if (bizCode === 'VOICE_NOT_FOUND') return ErrorCode.TTS_VOICE_NOT_FOUND;
  if (bizCode === 'SIGNED_URL_EXPIRED') return ErrorCode.FILE_SIGNED_URL_EXPIRED;
  if (bizCode === 'PAY_3DS_REQUIRED') return ErrorCode.PAY_3DS_REQUIRED;
  if (bizCode === 'TTS_TEXT_TOO_LONG') return ErrorCode.TTS_TEXT_TOO_LONG;
  if (bizCode === 'FILE_TOO_LARGE') return ErrorCode.FILE_TOO_LARGE;
  if (bizCode === 'FILE_FORMAT_UNSUPPORTED') return ErrorCode.FILE_FORMAT_UNSUPPORTED;
  if (bizCode === 'MEMBERSHIP_REQUIRED') return ErrorCode.MEMBERSHIP_REQUIRED;

  // 文本兜底（轻规则）
  if (message?.includes('timeout')) return ErrorCode.TIMEOUT;
  if (message?.includes('network')) return ErrorCode.NETWORK;
  if (message?.includes('quota')) return ErrorCode.QUOTA_EXHAUSTED;

  // HTTP状态码兜底
  return getErrorCodeByHttpStatus(httpStatus);
}



/**
 * 格式化错误消息
 */
export function formatErrorMessage(error: any, context?: string): string {
  if (!error) return '未知错误';

  if (typeof error === 'string') return error;

  if (error.message) return error.message;

  if (error.response?.data?.message) return error.response.data.message;

  if (context) return `${context}: ${JSON.stringify(error)}`;

  return JSON.stringify(error);
}

/**
 * 获取用户友好的错误消息（基于AppError）
 */
export function getUserFriendlyMessage(error: AppError | any): string {
  // 如果是AppError，直接返回messageForUser
  if (error && typeof error === 'object' && 'messageForUser' in error) {
    return error.messageForUser || error.message;
  }

  // 根据HTTP状态码提供默认消息
  if (error.response?.status) {
    const status = error.response.status;
    if (status >= 500) return '服务器内部错误，请稍后重试';
    if (status === 404) return '请求的资源不存在';
    if (status === 403) return '权限不足，无法访问';
    if (status === 401) return '身份验证失败，请重新登录';
    if (status >= 400) return '请求参数错误';
  }

  return formatErrorMessage(error);
}

/**
 * 错误分类检查函数
 */
export function isNetworkError(error: any): boolean {
  return !!(error.code === 'NETWORK_ERROR' || 
           error.message?.includes('Network Error') ||
           error.message?.includes('fetch'));
}

export function isTimeoutError(error: any): boolean {
  return !!(error.code === 'TIMEOUT_ERROR' || 
           error.message?.includes('timeout'));
}

export function isAuthError(error: any): boolean {
  return !!(error.response?.status === 401 || 
           error.code === 'AUTH_ERROR');
}

export function isPermissionError(error: any): boolean {
  return !!(error.response?.status === 403 || 
           error.code === 'PERMISSION_ERROR');
}

export function isServerError(error: any): boolean {
  return !!(error.response?.status >= 500 || 
           error.code === 'SERVER_ERROR');
}



/**
 * 检查错误是否可重试
 */
export function isRetryableError(error: any): boolean {
  return isNetworkError(error) || isTimeoutError(error) || isServerError(error);
}

/**
 * 计算重试延迟时间
 */
export function calculateRetryDelay(
  attempt: number, 
  baseDelay = 1000,
  strategy: 'linear' | 'exponential' = 'exponential'
): number {
  if (strategy === 'linear') {
    return baseDelay * attempt;
  }
  
  // 指数退避，最大延迟30秒
  return Math.min(baseDelay * Math.pow(2, attempt - 1), 30000);
}

// ============= 消息管理 =============

// 默认消息配置
export const DEFAULT_MESSAGE_CONFIG: Record<MessageType, MessageTypeConfig> = {
  success: { duration: 3000, showClose: false, center: false },
  info: { duration: 3000, showClose: false, center: false },
  warning: { duration: 4000, showClose: true, center: false },
  error: { duration: 5000, showClose: true, center: false }
};

// 消息模板
export const MESSAGE_TEMPLATES = {
  SAVE_SUCCESS: { type: 'success' as const, message: '保存成功' },
  SAVE_FAILED: { type: 'error' as const, message: '保存失败' },
  DELETE_SUCCESS: { type: 'success' as const, message: '删除成功' },
  DELETE_FAILED: { type: 'error' as const, message: '删除失败' },
  LOAD_FAILED: { type: 'error' as const, message: '加载失败' },
  NETWORK_ERROR: { type: 'error' as const, message: '网络连接失败' },
  PERMISSION_DENIED: { type: 'warning' as const, message: '权限不足' },
  OPERATION_SUCCESS: { type: 'success' as const, message: '操作成功' },
  OPERATION_FAILED: { type: 'error' as const, message: '操作失败' }
};

/**
 * 合并消息配置
 */
export function mergeMessageConfig(
  type: MessageType,
  customConfig: Partial<MessageConfig> = {}
): MessageConfig {
  const defaultConfig = DEFAULT_MESSAGE_CONFIG[type];
  return {
    type,
    message: '',
    ...defaultConfig,
    ...customConfig
  };
}

/**
 * 创建消息配置
 */
export function createMessageConfig(
  type: MessageType,
  message: string,
  options: Partial<MessageConfig> = {}
): MessageConfig {
  return mergeMessageConfig(type, { message, ...options });
}

/**
 * 创建队列消息
 */
export function createQueuedMessage(
  type: MessageType,
  message: string,
  config?: Partial<MessageConfig>
): QueuedMessage {
  return {
    id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    type,
    message,
    timestamp: Date.now(),
    config
  };
}

/**
 * 检查是否为重复消息
 */
export function isDuplicateMessage(
  newMessage: QueuedMessage,
  messageQueue: QueuedMessage[]
): boolean {
  const duplicateWindow = 3000; // 3秒内的消息视为重复
  const now = Date.now();

  return messageQueue.some(msg =>
    msg.type === newMessage.type &&
    msg.message === newMessage.message &&
    (now - msg.timestamp) < duplicateWindow
  );
}

/**
 * 检查消息是否过期
 */
export function isMessageExpired(message: QueuedMessage, maxAge: number = 300000): boolean {
  return (Date.now() - message.timestamp) > maxAge;
}

/**
 * 计算消息统计
 */
export function calculateMessageStats(messageQueue: QueuedMessage[]): MessageStats {
  const now = Date.now();
  const fiveMinutesAgo = now - 5 * 60 * 1000;

  const byType: Record<MessageType, number> = {
    success: 0,
    warning: 0,
    info: 0,
    error: 0
  };

  let recent = 0;

  messageQueue.forEach(msg => {
    byType[msg.type]++;
    if (msg.timestamp > fiveMinutesAgo) {
      recent++;
    }
  });

  return {
    total: messageQueue.length,
    byType,
    recent
  };
}

/**
 * 显示消息
 */
export function showMessage(config: MessageConfig): void {
  ElMessage(config);
}

/**
 * 显示通知
 */
export function showNotification(config: MessageConfig): void {
  ElNotification({
    title: config.title,
    message: config.message,
    type: config.type,
    duration: config.duration,
    showClose: config.showClose
  });
}

// ============= 导出工具集合 =============

export const notifyUtils = {
  // 错误构建和推断
  buildAppError,
  getErrorCodeByHttpStatus,
  inferErrorCode,

  // 错误处理
  formatErrorMessage,
  getUserFriendlyMessage,
  isNetworkError,
  isTimeoutError,
  isAuthError,
  isPermissionError,
  isServerError,
  isRetryableError,
  calculateRetryDelay,

  // 消息管理
  mergeMessageConfig,
  createMessageConfig,
  showMessage,
  showNotification,

  // 常量
  DEFAULT_MESSAGE_CONFIG,
  MESSAGE_TEMPLATES
};

export default notifyUtils;
